import React, { useEffect, useState } from 'react';
import { generatePath, useParams, useRouteMatch } from 'react-router-dom';
import {
  ActionButton,
  Panel,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  PanelTitleButton,
  PanelTitleButtonsGroup,
  PanelTitleWrapper
} from 'components/panel';
import { Breadcrumb, Breadcrumbs } from 'components/breadcrumbs';
import { Tab, TabList, TabPanel, TabPanelBody, Tabs } from 'components/tabs';
import { useMedicalContext } from 'contexts/MedicalContext';
import { FormattedMessage } from 'react-intl';
import messages from 'messages';
import {
  ROUTE_PERSONS_ENTITY,
  ROUTE_PERSONS_ENTITY_PERSON,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL_EDIT
} from 'routes/RouteList';
import useTabIndex from 'hooks/utils/useTabIndex';
import { getLocalDateFormat } from 'utils/date';
import PersonIssue from 'models/PersonIssue';
import IssueDetailsTab from 'containers/pages/rehab/issue/IssueDetailsTab';
import { useEntityContext } from 'containers/pages/persons/store/useEntityContext';
import usePerson from 'hooks/queries/usePerson';
import ContextMenuWrapper from 'components/menu/ContextMenuWrapper';
import ContextMenu from 'components/menu/ContextMenu';

function Issue() {
  const { entityId, groupId, personId, issueId } = useParams();
  const { fetchPerson } = usePerson({ personId: personId });
  const person = useEntityContext(s => s.person);
  const { medical } = useMedicalContext();
  const [personIssue, setPersonIssue] = useState(new PersonIssue({}));
  const active = useRouteMatch([
    ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL_EDIT,
    ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL
  ]);
  const edit = useRouteMatch(
    ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL_EDIT
  )?.isExact;
  const [tabIndex, setTabIndex] = useTabIndex([
    ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL
  ]);

  useEffect(() => {
    const fetchPersonData = async () => {
      await fetchPerson(personId);
    };
    if (!person.id) {
      fetchPersonData();
    }
  }, [entityId, personId]);

  useEffect(() => {
    if (medical?.issues) {
      const personIssue = medical.issues.find(i => i.id === issueId);
      if (personIssue) {
        setPersonIssue(personIssue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medical, issueId]);

  return (
    <Panel active={active?.isExact}>
      {person && (
        <>
          <PanelHeader>
            <Breadcrumbs>
              <Breadcrumb
                to={generatePath(ROUTE_PERSONS_ENTITY, { entityId, groupId })}
              >
                <FormattedMessage {...messages.breadcrumbSporters} />
              </Breadcrumb>
              <Breadcrumb
                to={generatePath(ROUTE_PERSONS_ENTITY_PERSON, {
                  entityId,
                  groupId,
                  personId
                })}
              >
                {`${person.firstname} ${person.lastname}`}
              </Breadcrumb>
            </Breadcrumbs>
            <PanelTitleWrapper>
              <PanelTitle>{personIssue.title}</PanelTitle>
              <PanelSubtitle>
                {getLocalDateFormat(personIssue.injuryDate)}
                {personIssue.injuryEndDate && (
                  <>
                    {' - '}
                    {getLocalDateFormat(personIssue.injuryEndDate)}
                  </>
                )}
              </PanelSubtitle>
            </PanelTitleWrapper>
            <PanelTitleButtonsGroup>
              <ActionButton />
              <ContextMenuWrapper trigger={<PanelTitleButton />}>
                <ContextMenu />
              </ContextMenuWrapper>
            </PanelTitleButtonsGroup>
          </PanelHeader>
          <PanelBody>
            <Tabs
              selectedIndex={tabIndex}
              onSelect={index => setTabIndex(index)}
            >
              <TabList>
                <Tab key="issue.detail">
                  <FormattedMessage {...messages.rehabIssueDetailsTab} />
                </Tab>
              </TabList>
              <TabPanel key="issue.detail">
                <TabPanelBody withScroller>
                  <IssueDetailsTab
                    entityId={entityId}
                    personIssue={personIssue}
                    edit={edit}
                    medical={medical}
                    personId={personId}
                  />
                </TabPanelBody>
              </TabPanel>
            </Tabs>
          </PanelBody>
        </>
      )}
    </Panel>
  );
}

export default Issue;
